import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isoWeek from 'dayjs/plugin/isoWeek';
import { TradingHoursDTO } from '../DTOs';
import { TradingHours, TradingHourTime } from '../models';

export interface AdjustedTradingHours {
	[day: string]: { startDateTime: string; endDateTime: string } | undefined;
}

export const dayOfWeekKeys = [
	'monday',
	'tuesday',
	'wednesday',
	'thursday',
	'friday',
	'saturday',
	'sunday',
] as (keyof TradingHours)[];

export const adjustTradingHoursForCurrentDay = (tradingHours: TradingHours[]): AdjustedTradingHours => {
	dayjs.extend(isoWeek);
	const adjustedHours: AdjustedTradingHours = {};
	const today = dayjs();

	for (const [index, dayOfWeek] of dayOfWeekKeys.entries()) {
		// if current day of week has passed in the first week, then use the second week's hours
		const currentDayOfWeek = today.isoWeekday() - 1; // 0 - 6 starting on Monday
		const currentHoursWeek = currentDayOfWeek <= index ? tradingHours[0] : tradingHours[1];

		// day difference from today
		const dayDifference = currentDayOfWeek > index ? index - currentDayOfWeek + 7 : index - currentDayOfWeek;
		const dateForDayOfWeek = today.add(dayDifference, 'day').format('YYYY-MM-DD');
		const timesForDayOfWeek = currentHoursWeek[dayOfWeek] as TradingHourTime;

		adjustedHours[dayOfWeek] = {
			startDateTime: `${dateForDayOfWeek} ${timesForDayOfWeek?.startTime}`,
			endDateTime: `${dateForDayOfWeek} ${timesForDayOfWeek?.endTime}`,
		};
	}

	return adjustedHours;
};

export const formatOpeningHours = (hours?: { startDateTime: string; endDateTime: string }): string => {
	dayjs.extend(isSameOrAfter);

	if (!hours || hours?.startDateTime.includes('undefined')) {
		return 'Closed';
	}

	const startTimeDate = dayjs(`${hours.startDateTime}`);
	const endTimeDate = dayjs(`${hours.endDateTime}`);
	const midnight = startTimeDate.set('hour', 0);
	const midday = startTimeDate.set('hour', 12);
	const endOfDay = startTimeDate.set('hour', 23).set('minute', 59).set('second', 59);

	// If the store is closed for the day the API returns "00:00:00" for both times
	if (startTimeDate.isSame(midnight) && endTimeDate.isSame(midnight)) {
		return 'Closed';
	}

	// If store is opened for 24 hours the API returns starttime of "00:00:00" and endtime of "23:59:59"
	if (startTimeDate.isSame(midnight) && endTimeDate.isSame(endOfDay)) {
		return '24 hours';
	}

	let niceOpening = startTimeDate.format('h:mma');
	let niceClosing = endTimeDate.format('h:mma');

	// Parse midday (12:00:00) and midnight (00:00:00) to their respective string representations
	if (startTimeDate.isSame(midday)) {
		niceOpening = 'Midday';
	}
	if (startTimeDate.isSame(midnight) || startTimeDate.isSame(endOfDay)) {
		niceOpening = 'Midnight';
	}
	if (endTimeDate.isSame(midday)) {
		niceClosing = 'Midday';
	}
	if (endTimeDate.isSame(midnight) || endTimeDate.isSame(endOfDay)) {
		niceClosing = 'Midnight';
	}

	return `${niceOpening} - ${niceClosing}`;
};

export const getCurrentWeekTradingHours = (tradingHours: TradingHours[]): TradingHoursDTO[] => {
	const currentHoursWeek = adjustTradingHoursForCurrentDay(tradingHours);

	return dayOfWeekKeys.map((dayOfWeek) => ({
		day: `${dayOfWeek[0].toUpperCase()}${dayOfWeek.slice(1)}`, // uppercase the day of week
		hours: formatOpeningHours(currentHoursWeek[dayOfWeek]),
	}));
};
